import React from 'react';

/**
 * Boot State Machine - Explicit finite state machine for application boot
 * Prevents infinite loops and provides deterministic boot progression
 */

export type BootPhase = 
  | 'INIT' 
  | 'STORAGE_OK' 
  | 'HYDRATED' 
  | 'HYDRATION_TIMEOUT' 
  | 'AUTH_START' 
  | 'AUTH_READY' 
  | 'LICENSE_CHECK' 
  | 'LICENSED' 
  | 'UNLICENSED' 
  | 'ERROR';

export interface BootState {
  phase: BootPhase;
  timestamp: number;
  error?: Error;
  canTransitionTo(nextPhase: BootPhase): boolean;
  transition(nextPhase: BootPhase, error?: Error): BootState;
  isTerminal(): boolean;
}

class BootStateImpl implements BootState {
  constructor(
    public phase: BootPhase,
    public timestamp: number = Date.now(),
    public error?: Error
  ) {}

  canTransitionTo(nextPhase: BootPhase): boolean {
    const validTransitions: Record<BootPhase, BootPhase[]> = {
      'INIT': ['STORAGE_OK', 'ERROR'],
      'STORAGE_OK': ['HYDRATED', 'HYDRATION_TIMEOUT', 'ERROR'],
      'HYDRATED': ['AUTH_START', 'ERROR'],
      'HYDRATION_TIMEOUT': ['AUTH_START', 'ERROR'],
      'AUTH_START': ['AUTH_READY', 'ERROR'],
      'AUTH_READY': ['LICENSE_CHECK', 'ERROR'],
      'LICENSE_CHECK': ['LICENSED', 'UNLICENSED', 'ERROR'],
      'LICENSED': [], // Terminal
      'UNLICENSED': [], // Terminal
      'ERROR': [] // Terminal
    };

    return validTransitions[this.phase]?.includes(nextPhase) || false;
  }

  transition(nextPhase: BootPhase, error?: Error): BootState {
    if (!this.canTransitionTo(nextPhase)) {
      console.warn(`[BOOT_SM] Invalid transition from ${this.phase} to ${nextPhase}`);
      return this;
    }

    console.log(`[BOOT_SM] ${this.phase} → ${nextPhase}`, { ts: Date.now(), error: error?.message });
    return new BootStateImpl(nextPhase, Date.now(), error);
  }

  isTerminal(): boolean {
    return ['LICENSED', 'UNLICENSED', 'ERROR'].includes(this.phase);
  }
}

/**
 * Global boot state manager with one-shot transitions
 */
class BootStateMachine {
  private state: BootState = new BootStateImpl('INIT');
  private listeners: Array<(state: BootState) => void> = [];
  private transitionHistory: BootPhase[] = ['INIT'];

  getCurrentState(): BootState {
    return this.state;
  }

  getPhase(): BootPhase {
    return this.state.phase;
  }

  isTerminal(): boolean {
    return this.state.isTerminal();
  }

  canTransition(nextPhase: BootPhase): boolean {
    return this.state.canTransitionTo(nextPhase);
  }

  transition(nextPhase: BootPhase, error?: Error): boolean {
    if (this.state.isTerminal()) {
      console.warn(`[BOOT_SM] Cannot transition from terminal state ${this.state.phase}`);
      return false;
    }

    if (!this.state.canTransitionTo(nextPhase)) {
      console.warn(`[BOOT_SM] Invalid transition from ${this.state.phase} to ${nextPhase}`);
      return false;
    }

    this.state = this.state.transition(nextPhase, error);
    this.transitionHistory.push(nextPhase);
    
    // Notify listeners
    this.listeners.forEach(listener => {
      try {
        listener(this.state);
      } catch (err) {
        console.error('[BOOT_SM] Listener error:', err);
      }
    });

    return true;
  }

  subscribe(listener: (state: BootState) => void): () => void {
    this.listeners.push(listener);
    
    // Immediately call with current state
    listener(this.state);
    
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  getHistory(): BootPhase[] {
    return [...this.transitionHistory];
  }

  reset(): void {
    this.state = new BootStateImpl('INIT');
    this.transitionHistory = ['INIT'];
    console.log('[BOOT_SM] Reset to INIT');
  }
}

// Global singleton
export const bootStateMachine = new BootStateMachine();

// Convenience hooks for React components
export const useBootState = (): BootState => {
  const [state, setState] = React.useState(bootStateMachine.getCurrentState());
  
  React.useEffect(() => {
    return bootStateMachine.subscribe(setState);
  }, []);
  
  return state;
};
